import React, { useEffect, useState } from 'react';
import { Download, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { errMsg } from '@/services/staffAttendanceApi';
import { jobberMasterApi } from '@/services/jobberMasterApi';

const Result = ({ r }) => (
  <div className="space-y-2" data-testid="jobber-import-result">
    <div className="grid grid-cols-4 gap-2 text-center">
      {[['Rows', r.total, 'text-gray-900'], ['Added', r.created, 'text-emerald-700'], ['Updated', r.updated, 'text-sky-700'], ['Skipped', r.errors.length, 'text-rose-700']].map(([l, n, c]) => (
        <div key={l} className="rounded-md border bg-white py-2"><div className={`text-xl font-bold ${c}`} data-testid={`jobber-import-${l.toLowerCase()}`}>{n}</div><div className="text-[11px] text-gray-500">{l}</div></div>
      ))}
    </div>
    {r.unchanged > 0 && <p className="text-xs text-gray-500">{r.unchanged} row(s) had no changes.</p>}
    {r.errors.length > 0 && (
      <div className="max-h-56 overflow-y-auto rounded-md border border-rose-200 bg-rose-50/60 p-2 space-y-1" data-testid="jobber-import-errors">
        {r.errors.map((e) => (
          <p key={e.row} className="text-xs text-rose-800" data-testid={`jobber-import-error-row-${e.row}`}><b>Row {e.row}</b>{e.name ? ` · ${e.name}` : ''} — {e.errors.join(' · ')}</p>
        ))}
      </div>
    )}
  </div>
);

export const JobberImportDialog = ({ open, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  useEffect(() => { if (open) { setFile(null); setRes(null); } }, [open]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await jobberMasterApi.upload(file);
      setRes(r);
      toast.success(`${r.created} added · ${r.updated} updated${r.errors.length ? ` · ${r.errors.length} skipped` : ''}`);
      onDone();
    } catch (e) { toast.error(errMsg(e, 'Upload failed')); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="jobber-import-dialog">
        <DialogHeader>
          <DialogTitle>Upload jobbers (Excel)</DialogTitle>
          <DialogDescription>One row per jobber. An existing Jobber Name is updated, a new one is added. Rows with mistakes are skipped and listed. Categories: comma separated (e.g. Processing Unit, Folding Unit) — blank keeps the current ones.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Button variant="outline" size="sm" onClick={() => jobberMasterApi.template().catch((e) => toast.error(errMsg(e, 'Download failed')))} data-testid="jobber-template-btn">
            <Download className="w-4 h-4 mr-1" />Download template
          </Button>
          <input type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setRes(null); }}
            className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[#2D5F4F] file:px-3 file:py-2 file:text-white" data-testid="jobber-import-file" />
          {res && <Result r={res} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="jobber-import-close">Close</Button>
          <Button onClick={run} disabled={busy || !file} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="jobber-import-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JobberImportDialog;
